import { memo, ReactNode } from 'react';
import { JSONContent } from '@tiptap/core';
import { Box, HStack, Icon, Image, Link, Text, VStack } from '@chakra-ui/react';
import { Download } from 'lucide-react';
import ReactPlayer from 'react-player/youtube';
import { getIconByFileType } from '../../tiptap/fileUtils';
import {
  ConversationMessage,
  ConversationMessagePersona,
} from '@duke-hq/ui/components/Conversation/ConversationMessage';
import { ConversationOptionButton } from '@duke-hq/ui/components/Conversation/ConversationOptionButton';
import {
  type FindRunCursor,
  markFindMatches,
  renderTiptapContent,
  type TiptapHighlight,
} from './renderTiptapContent';

const DUKE_FALLBACK: ConversationMessagePersona = { name: 'Duke', avatarUrl: null };

export interface StaticBlockContentProps {
  /** The node's tiptap document (data.tiptap). */
  tiptap: JSONContent | undefined;
  /** Reply-button options shown beneath the messages, if any. */
  options?: { id: string; title: string }[];
  /** Resolves a personaId to the name/avatar shown in the header. */
  getPersonaById: (personaId: string) => ConversationMessagePersona;
  /**
   * Resolves an uploaded media file to a displayable URL, given the stored
   * filename and (when present) the file DRN. Defaults to identity. The canvas
   * passes the same resolver the live editor uses so the two can't drift.
   */
  resolveMediaSrc?: (src: string, fileDrn?: unknown) => string;
  /**
   * Renders the ReactFlow source handle for an option, positioned at the
   * option's row so edges stay anchored. Omitted outside the canvas (e.g.
   * Storybook), where there is no ReactFlow context.
   */
  renderOptionHandle?: (optionId: string) => ReactNode;
  /** Find-in-conversation highlight; omitted when search is closed or this node has no hit. */
  highlight?: TiptapHighlight;
}

const personaIdOf = (block: JSONContent): string =>
  typeof block.attrs?.personaId === 'string' ? block.attrs.personaId : '';

// -- StaticBlockContent --------------------------------------------------------
// Read-only render of a flow node's content with no editor mounted, drawn to
// match the rendered student conversation: each message is a ConversationMessage
// bubble (the shared component the student runtime uses), media and files sit in
// their own bubbles, and reply options are right-aligned learner-choice pills.
// This is the always-mounted display half of the node.
function StaticBlockContentComponent({
  tiptap,
  options,
  getPersonaById,
  resolveMediaSrc = (src) => src,
  renderOptionHandle,
  highlight,
}: StaticBlockContentProps) {
  const blocks = tiptap?.content ?? [];
  // One cursor per node render; the ordinal spans bubbles then option titles.
  const runCursor: FindRunCursor = { nextRun: 0 };

  return (
    // A subtle tinted canvas behind the messages so the white conversation
    // bubbles read as bubbles, the way they do against the rendered
    // conversation (the node card itself is white). Lives here, on the shared
    // content, so the canvas and Storybook render identically.
    <Box bg="bg.subtle" borderRadius="md" p={2} width="100%">
      <VStack align="stretch" spacing={1} width="100%">
        {blocks.map((block, index) => {
          const personaId = personaIdOf(block);
          const persona = personaId.length > 0 ? getPersonaById(personaId) : DUKE_FALLBACK;
          const previous = blocks[index - 1];
          // Show the name and avatar on the first message of a speaker run (at
          // the top), matching where the live editor's persona header sits, so
          // the avatar does not jump when a node becomes the live editor.
          const showName = !previous || personaIdOf(previous) !== personaId;
          const showAvatar = showName;

          return (
            <ConversationMessage
              key={index}
              persona={persona}
              showName={showName}
              showAvatar={showAvatar}
              // Continuing messages show the same dashed circle as the live
              // editor (non-interactive here), so the read-only card matches
              // exactly what the node looks like once selected.
              continuesPersona={!showAvatar}
            >
              {renderBlock(block, resolveMediaSrc, highlight, runCursor)}
            </ConversationMessage>
          );
        })}

        {options && options.length > 0 ? (
          <VStack align="flex-end" spacing={2} pt={2}>
            {options.map((option) => (
              // The pill owns its own width (ConversationOptionButton hugs the
              // label up to OPTION_PILL_MAX_W); the wrapper only anchors the
              // ReactFlow source handle, so it must not impose its own width.
              <Box key={option.id} pos="relative" display="flex">
                <ConversationOptionButton onClick={() => {}}>
                  {markFindMatches(option.title, highlight, runCursor)}
                </ConversationOptionButton>
                {renderOptionHandle ? renderOptionHandle(option.id) : null}
              </Box>
            ))}
          </VStack>
        ) : null}
      </VStack>
    </Box>
  );
}

// Memoised so an unrelated node's edit (which replaces the whole nodes array and
// hands every card a fresh `data` reference) does not re-render the cards whose
// content is unchanged. The host passes stable resolver/handle callbacks so the
// default shallow comparison holds.
export const StaticBlockContent = memo(StaticBlockContentComponent);
StaticBlockContent.displayName = 'StaticBlockContent';

function renderBlock(
  block: JSONContent,
  resolveMediaSrc: (src: string, fileDrn?: unknown) => string,
  highlight: TiptapHighlight | undefined,
  runCursor: FindRunCursor,
) {
  if (block.type === 'resizableMedia') {
    const rawSrc = typeof block.attrs?.src === 'string' ? block.attrs.src : undefined;
    const src = rawSrc === undefined ? undefined : resolveMediaSrc(rawSrc, block.attrs?.fileDrn);
    const mediaType = block.attrs?.['media-type'];
    const width = typeof block.attrs?.width === 'string' ? block.attrs.width : undefined;
    const alt = typeof block.attrs?.alt === 'string' ? block.attrs.alt : '';

    if (mediaType === 'video') {
      return (
        <video src={src} controls style={{ maxWidth: '100%', width, borderRadius: '0.5rem' }} />
      );
    }
    if (mediaType === 'img') {
      return <Image src={src} alt={alt} maxW="100%" w={width} borderRadius="lg" />;
    }
    // Giphy and YouTube store a full URL in `src`, not an uploaded filename, so
    // they bypass the file resolver (matching ResizableMediaNodeView).
    if (mediaType === 'gif') {
      return <Image src={rawSrc} alt={alt} maxW="100%" w={width} borderRadius="lg" />;
    }
    if (mediaType === 'youtube') {
      const height = typeof block.attrs?.height === 'string' ? block.attrs.height : '150px';
      return (
        <Box w={width} h={height} maxW="100%" borderRadius="lg" overflow="hidden">
          <ReactPlayer url={rawSrc} width="100%" height="100%" controls />
        </Box>
      );
    }
    return (
      <Link href={rawSrc} isExternal>
        {alt || 'Attachment'}
      </Link>
    );
  }

  if (block.type === 'fileComponent') {
    const src =
      typeof block.attrs?.src === 'string'
        ? resolveMediaSrc(block.attrs.src, block.attrs?.fileDrn)
        : undefined;
    const title = typeof block.attrs?.title === 'string' ? block.attrs.title : 'File';
    const size = typeof block.attrs?.size === 'string' ? block.attrs.size : undefined;
    const sizeUnit = typeof block.attrs?.sizeUnit === 'string' ? block.attrs.sizeUnit : '';
    const type = typeof block.attrs?.type === 'string' ? block.attrs.type : undefined;
    // Matches FileTTComponent's preview rendering: an unstyled link with no hover
    // decoration, a file-type icon and a download affordance.
    return (
      <Link
        href={src}
        isExternal
        variant="unstyled"
        display="inline-flex"
        _hover={{ textDecoration: 'none' }}
      >
        <HStack align="center" spacing={2}>
          {type ? getIconByFileType(type) : null}
          <Box>
            <Text color="fg.default">{title}</Text>
            {size ? (
              <Text fontSize="xs" color="fg.muted">
                {size} {sizeUnit}
              </Text>
            ) : null}
          </Box>
          <Icon as={Download} color="fg.muted" />
        </HStack>
      </Link>
    );
  }

  return renderTiptapContent(block.content, highlight, runCursor);
}
